// src/features/task/create/PropertyPill.tsx
// A task property, presented the way a person thinks about it: a compact pill
// that reads "Assignee" when nothing is chosen and "Anna Petrova" once it is.
// The real control lives in a popover behind it, so eight properties cost eight
// short words on screen instead of eight labelled rows.
//
// One primitive, three looks: `pill` for a compact inline chip, `quiet` for a
// text-only menu trigger (Start from template), and `row` for the always-open
// properties panel, where the label is rendered outside by `PropertyRow` and
// the trigger is a full-width value that reads "не задано" while empty.
//
// Dismissal is deliberately portal-aware. Several of the controls placed inside
// (UserPicker, TagPicker) render their own dropdown into document.body, so a
// naive "click outside closes" would eat the very click that picks a value.
// Anything landing outside #root is another popover layer, not the page.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@core/ui';
import { Icon, CREATE_ICONS } from './icons';

const DEFAULT_POPOVER_WIDTH = 288;
const POPOVER_MAX_HEIGHT = 380;
/** Below this much room underneath, the popover flips above the trigger. */
const FLIP_THRESHOLD = 240;

/** True when a click landed in a portal layer that is not the page itself. */
function inOtherPortalLayer(target: Node): boolean {
  const root = document.getElementById('root');
  return !!root && document.body.contains(target) && !root.contains(target);
}

export interface PropertyPillProps {
  /** Stroke path from CREATE_ICONS. */
  icon: string;
  /** Extra classes for the icon — used to carry a semantic colour. */
  iconClassName?: string;
  /** A colour swatch shown instead of the icon (task type colours). */
  swatch?: string;
  /** What the property is. Shown alone, muted, while nothing is chosen. */
  label: string;
  /** The chosen value. When present it replaces the label on the pill. */
  value?: string | null;
  /** Marks the pill invalid; the message is rendered by the caller. */
  invalid?: boolean;
  /** DOM id of that message, so the pill announces it rather than only reddening. */
  errorId?: string;
  /** Shows a required marker beside the label. */
  required?: boolean;
  disabled?: boolean;
  /**
   * What the `row` variant shows while nothing is chosen. The row keeps its
   * place in the panel and says "не задано" — it never collapses, because a
   * property that can be set is information even when it is not set yet.
   */
  emptyText?: string;
  /** Heading inside the popover. Falls back to `label`. */
  popoverTitle?: string;
  /** One short line under the popover heading — never on the page. */
  popoverHint?: string;
  popoverWidth?: number;
  variant?: 'pill' | 'quiet' | 'row';
  className?: string;
  'data-field-error'?: string;
  /** Popover body. `close` dismisses it and returns focus to the trigger. */
  children: (close: () => void) => ReactNode;
}

export function PropertyPill({
  icon,
  iconClassName,
  swatch,
  label,
  value,
  invalid,
  errorId,
  required,
  disabled,
  popoverTitle,
  popoverHint,
  popoverWidth = DEFAULT_POPOVER_WIDTH,
  variant = 'pill',
  emptyText,
  className,
  children,
  ...rest
}: PropertyPillProps) {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const hasValue = Boolean(value && value.trim());

  const close = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const width = Math.min(popoverWidth, window.innerWidth - 16);
    const left = Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - width - 8));
    const spaceBelow = window.innerHeight - rect.bottom;
    const above = spaceBelow < FLIP_THRESHOLD && rect.top > spaceBelow;
    setStyle({
      position: 'fixed',
      left,
      width,
      ...(above ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
      maxHeight: Math.max(160, Math.min(POPOVER_MAX_HEIGHT, (above ? rect.top : spaceBelow) - 16)),
    });
  }, [popoverWidth]);

  useEffect(() => {
    if (!open) return;
    updatePosition();

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      // The trigger toggles itself — closing here would fight its own click.
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      if (inOtherPortalLayer(target)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Only ours if focus is still inside it; a nested picker gets Escape first.
      if (!popoverRef.current?.contains(document.activeElement)) return;
      close();
    };
    const reposition = () => updatePosition();

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);

    const raf = window.requestAnimationFrame(() => {
      const node = popoverRef.current;
      const focusable = node?.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable ?? node)?.focus();
    });

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      window.cancelAnimationFrame(raf);
    };
  }, [open, close, updatePosition]);

  /** Required and still empty — marked before Create is ever pressed. */
  const missingRequired = Boolean(required) && !hasValue && !invalid;

  const triggerClass =
    variant === 'quiet'
      ? cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-md px-2 py-1 text-sm transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          hasValue ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          'hover:bg-muted',
          open && 'bg-muted text-foreground',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )
      : variant === 'row'
        ? cn(
            // A panel row is a full-width value, not a chip: the label lives
            // outside it (PropertyRow), so the trigger carries only the answer.
            'flex w-full items-center gap-2 rounded-md border px-2 py-1.5 text-left text-sm transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            hasValue ? 'text-foreground' : 'text-muted-foreground',
            open
              ? 'border-primary/60 bg-muted text-foreground'
              : 'border-transparent hover:border-border hover:bg-muted hover:text-foreground',
            missingRequired && !open && 'border-dashed border-destructive/50',
            invalid && 'border-solid border-destructive text-destructive',
          )
        : cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50',
          hasValue
            ? 'border-border bg-muted/60 text-foreground'
            : 'border-dashed border-border text-muted-foreground',
          'hover:border-primary/50 hover:bg-muted hover:text-foreground',
          open && 'border-primary/60 bg-muted text-foreground',
          invalid && 'border-destructive text-destructive hover:border-destructive',
        );

  return (
    <>
      <button
        {...rest}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        aria-describedby={invalid ? errorId : undefined}
        // The pill shows the value; the label is what the value *is*, so both
        // belong in the accessible name. An empty row still announces what it
        // would set, and that it is unset.
        aria-label={hasValue ? `${label}: ${value}` : emptyText ? `${label}: ${emptyText}` : label}
        className={cn(triggerClass, className)}
      >
        {swatch ? (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
            style={{ backgroundColor: swatch }}
            aria-hidden="true"
          />
        ) : (
          <Icon d={icon} className={cn('h-3.5 w-3.5 shrink-0', iconClassName)} />
        )}
        <span className={cn('truncate', variant === 'row' && 'min-w-0 flex-1')}>
          {hasValue ? value : variant === 'row' ? (emptyText ?? label) : label}
        </span>
        {/* In a row the asterisk belongs beside the label, which PropertyRow owns. */}
        {required && !hasValue && variant !== 'row' && (
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        )}
        {(variant === 'quiet' || variant === 'row') && (
          <Icon
            d={CREATE_ICONS.chevronDown}
            className="h-3.5 w-3.5 shrink-0 opacity-50 transition-opacity"
          />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            id={popoverId}
            role="dialog"
            aria-label={popoverTitle ?? label}
            tabIndex={-1}
            style={style}
            className={cn(
              'z-50 flex flex-col gap-2 overflow-y-auto rounded-lg border border-border p-3 shadow-lg',
              'bg-popover text-popover-foreground focus:outline-none',
            )}
          >
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {popoverTitle ?? label}
            </p>
            {popoverHint && <p className="text-xs text-muted-foreground">{popoverHint}</p>}
            {children(close)}
          </div>,
          document.body,
        )}
    </>
  );
}

// ---------------------------------------------------------------------------
// A list of choices inside a popover
// ---------------------------------------------------------------------------

export interface PillOption {
  value: string;
  label: string;
  /** Secondary line — where a template comes from, a category code, … */
  hint?: string;
  icon?: string;
  iconClassName?: string;
  swatch?: string;
}

export interface PillOptionListProps {
  options: PillOption[];
  value: string;
  onSelect: (value: string) => void;
  /** Accessible name — the popover heading is decorative text, not a label. */
  ariaLabel: string;
}

/**
 * Single-choice list. Real buttons in a radiogroup: Tab reaches the group,
 * arrows move within it, Enter and Space pick — no custom focus trickery and
 * no reliance on hover.
 */
export function PillOptionList({ options, value, onSelect, ariaLabel }: PillOptionListProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const move = (index: number, delta: number) => {
    const next = (index + delta + options.length) % options.length;
    const buttons = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    buttons?.[next]?.focus();
  };

  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  return (
    <div ref={groupRef} role="radiogroup" aria-label={ariaLabel} className="-mx-1 flex flex-col">
      {options.map((option, index) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value || `__${index}`}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={index === activeIndex ? 0 : -1}
            onClick={() => onSelect(option.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
                e.preventDefault();
                move(index, 1);
              } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
                e.preventDefault();
                move(index, -1);
              }
            }}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              selected ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent/50',
            )}
          >
            {option.swatch ? (
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                style={{ backgroundColor: option.swatch }}
                aria-hidden="true"
              />
            ) : option.icon ? (
              <Icon
                d={option.icon}
                className={cn('h-4 w-4 shrink-0', option.iconClassName ?? 'text-muted-foreground')}
              />
            ) : null}
            <span className="min-w-0 flex-1">
              <span className="block truncate">{option.label}</span>
              {option.hint && (
                <span className="block truncate text-xs text-muted-foreground">{option.hint}</span>
              )}
            </span>
            {selected && <Icon d={CREATE_ICONS.check} className="h-3.5 w-3.5 shrink-0" />}
          </button>
        );
      })}
    </div>
  );
}
